import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { getFormattedMonth, getShouldShortcircuitMonth, focusNextInput, getFormattedDay, getShouldShortcircuitDay, getFormattedYear, commonInputProps, triggerOnBlur } from './utils.js';
import { onKeyDownHelper } from './helpers.js';
import { DateInputFluidContainer, DateInputWrapper, DateInputInput, DateInputDivider } from './styled.js';
import { DateInputsPropTypes } from './propsTypes.js';
import '@babel/runtime/helpers/esm/toConsumableArray';
import '@elliemae/ds-utilities/timeUtils';
import '@elliemae/ds-utilities/utils';
import 'lodash';
import 'styled-components';
import '@elliemae/ds-grid';
import 'prop-types';

var FORMAT = 'MMDDYYYY';
var DateInputs = function DateInputs(_ref) {
  var onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      onDateChange = _ref.onDateChange,
      disabled = _ref.disabled,
      onCustomKeyDown = _ref.onCustomKeyDown,
      time = _ref.time,
      tabIndex = _ref.tabIndex,
      innerRef = _ref.innerRef;

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      month = _useState2[0],
      setMonth = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      day = _useState4[0],
      setDay = _useState4[1];

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      year = _useState6[0],
      setYear = _useState6[1];

  var _useState7 = useState({}),
      _useState8 = _slicedToArray(_useState7, 2),
      onChangeDateTrigger = _useState8[0],
      setOnChangeDateTrigger = _useState8[1];

  var dayRef = React.useRef();

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      haveInputsOnChangeTriggered = _useState10[0],
      setHaveInputsOnChangeTriggered = _useState10[1];

  var digits = 2;
  var placeholder = '';
  var handleFluidClick = React.useCallback(function (e) {
    if (!e.target.type && dayRef.current) {
      dayRef.current.focus();
    }
  }, [dayRef.current]);
  var triggerOnDateChange = React.useCallback(function () {
    if (month && day && year && haveInputsOnChangeTriggered) {
      var momentValue = moment("".concat(month).concat(day).concat(year), 'MMDDYYYY', true);

      if (momentValue.isValid()) {
        onDateChange(momentValue);
      }

      setHaveInputsOnChangeTriggered(false);
    }
  }, [day, month, year, haveInputsOnChangeTriggered]); // isArrowChange is coming from the onKeyDown handler in helpers

  var onMonthChange = React.useCallback(function (e, isArrowChange) {
    var value = getFormattedMonth(e.target.value);
    var shouldAdvance = getShouldShortcircuitMonth(e.target.value);
    setMonth(value);
    setHaveInputsOnChangeTriggered(true);

    if (e.target.value.length > 1 || shouldAdvance) {
      if (!isArrowChange) focusNextInput(e.target);
      setOnChangeDateTrigger({});
    }
  }, []); // isArrowChange is coming from the onKeyDown handler in helpers

  var onDayChange = React.useCallback(function (e, isArrowChange) {
    var value = getFormattedDay(e.target.value);
    var shouldAdvance = getShouldShortcircuitDay(e.target.value);
    setDay(value);
    setHaveInputsOnChangeTriggered(true);

    if (e.target.value.length > 1 || shouldAdvance) {
      if (!isArrowChange) focusNextInput(e.target);
      setOnChangeDateTrigger({});
    }
  }, []);
  var onYearChange = React.useCallback(function (e) {
    var value = getFormattedYear(e.target.value);
    setYear(value);
    setHaveInputsOnChangeTriggered(true);
    setOnChangeDateTrigger({});
  }, []);
  useEffect(function () {
    // When component mounts haveInputsOnChangeTriggered is set to false
    // so this is not triggered on mount but is triggered with all other changes
    if (haveInputsOnChangeTriggered) {
      var momentValue = moment("".concat(month).concat(day).concat(year), FORMAT, true);
      if (month || day || year) onChange("".concat(month).concat(day).concat(year), momentValue.isValid() ? momentValue : null);
    }
  }, [day, month, year]);
  useEffect(function () {
    triggerOnDateChange();
  }, [onChangeDateTrigger]);
  useEffect(function () {
    if (time && time !== "".concat(month).concat(day).concat(year) && moment(time, 'MMDDYYYY').isValid()) {
      var values = String(time || '');
      if (values.slice(0, 2)) setMonth(values.slice(0, 2));
      if (values.slice(2, 4)) setDay(values.slice(2, 4));
      if (values.slice(4, 8)) setYear(values.slice(4, 8));
    } else if (!time && (month || day || year)) {
      setMonth('');
      setDay('');
      setYear('');
    }
  }, [time]);

  var onKeyDown = function onKeyDown(event) {
    return onKeyDownHelper(event, {
      onDayChange: onDayChange,
      onMonthChange: onMonthChange,
      onYearChange: onYearChange
    });
  };

  var renderMonthInput = function renderMonthInput() {
    placeholder = 'MM';

    var onMonthInputBlur = function onMonthInputBlur(e) {
      setOnChangeDateTrigger({});
      triggerOnBlur(e, onBlur);
    };

    return /*#__PURE__*/React.createElement(DateInputInput, _extends({}, commonInputProps(digits, onKeyDown, placeholder), {
      autoComplete: "off",
      key: "month-input",
      "data-testid": "month",
      disabled: disabled,
      name: "month",
      onBlur: onMonthInputBlur,
      onChange: onMonthChange,
      value: month,
      tabIndex: tabIndex,
      ref: dayRef
    }));
  };

  var renderDayInput = function renderDayInput() {
    placeholder = 'DD';

    var onDayInputBlur = function onDayInputBlur(e) {
      setOnChangeDateTrigger({});
      triggerOnBlur(e, onBlur);
    };

    return /*#__PURE__*/React.createElement(DateInputInput, _extends({}, commonInputProps(digits, onKeyDown, placeholder), {
      autoComplete: "off",
      key: "day-input",
      "data-testid": "day",
      disabled: disabled,
      name: "day",
      onBlur: onDayInputBlur,
      onChange: onDayChange,
      value: day,
      tabIndex: tabIndex
    }));
  };

  var renderYearInput = function renderYearInput() {
    placeholder = 'YY';
    digits = 4;

    var onYearInputBlur = function onYearInputBlur(e) {
      triggerOnBlur(e, onBlur);
    };

    return /*#__PURE__*/React.createElement(DateInputInput, _extends({}, commonInputProps(digits, onKeyDown, placeholder), {
      autoComplete: "off",
      key: "year-input",
      "data-testid": "year",
      disabled: disabled,
      name: "year",
      onBlur: onYearInputBlur,
      onChange: onYearChange,
      value: year,
      size: "l",
      tabIndex: tabIndex
    }));
  };

  var renderDateInputs = function renderDateInputs() {
    var inputs = [renderMonthInput(), renderDayInput(), renderYearInput()];
    var inputsWithDividers = inputs.reduce(function (acc, input, index) {
      return input ? acc.concat([index ? /*#__PURE__*/React.createElement(DateInputDivider, null, "/") : null, input]) : acc;
    }, []);
    return [inputsWithDividers, /*#__PURE__*/React.createElement('span', {
      key: 'span'
    }) // hack for next/prev focus
    ];
  };

  return /*#__PURE__*/React.createElement(DateInputFluidContainer, {
    onClick: handleFluidClick,
    "data-testid": "date-input-fluidContainer"
  }, /*#__PURE__*/React.createElement(DateInputWrapper, {
    disabled: disabled,
    ref: innerRef,
    onKeyDown: onCustomKeyDown,
    tabIndex: -1,
    cols: ['auto', '6px', 'auto', '6px', 'auto']
  }, renderDateInputs()));
};
DateInputs.propTypes = DateInputsPropTypes;

export { DateInputs };
//# sourceMappingURL=DateInputs.js.map
